import React, { useState, useEffect } from 'react';
import { doc, updateDoc, deleteDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

function WorkItemModal({ item, onClose, onUpdate, allItems, availableTags, onNavigateToItem, onShowRelatedItems }) {
  const [showLinks, setShowLinks] = useState(false);
  const [showChildren, setShowChildren] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [showParent, setShowParent] = useState(false);
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [editingLinkIndex, setEditingLinkIndex] = useState(null);
  const [linkMenuIndex, setLinkMenuIndex] = useState(null);
  const [newTag, setNewTag] = useState('');
  const [localTags, setLocalTags] = useState(availableTags);
  const [formData, setFormData] = useState({
    title: item.title,
    type: item.type,
    division: item.division || '',
    status: item.status,
    description: item.description || '',
    tags: item.tags || [],
    parentId: item.parentId || null,
    parentType: item.parentType || null
  });

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Update formData when item changes (e.g., when navigating to a child)
  useEffect(() => {
    setFormData({
      title: item.title,
      type: item.type,
      division: item.division || '',
      status: item.status,
      description: item.description || '',
      tags: item.tags || [],
      parentId: item.parentId || null,
      parentType: item.parentType || null
    });
    // Reset other states when item changes
    setShowLinks(false);
    setShowChildren(false);
    setShowTags(false);
    setShowParent(false);
    setEditingLinkIndex(null);
    setLinkMenuIndex(null);
    setLocalTags(availableTags);
  }, [item.id, availableTags]);

  // Close link menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (linkMenuIndex !== null && !e.target.closest('.tag')) {
        setLinkMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [linkMenuIndex]);

  const handleAddLink = async () => {
    if (!newLinkUrl.trim()) {
      alert('Please enter a URL');
      return;
    }
    
    const link = {
      title: newLinkTitle.trim() || newLinkUrl,
      url: newLinkUrl.trim(),
      addedAt: new Date().toISOString()
    };

    await onUpdate(item.id, {
      links: arrayUnion(link)
    });
    setNewLinkTitle('');
    setNewLinkUrl('');
  };

  const handleDeleteLink = async (linkIndex) => {
    const updatedLinks = (item.links || []).filter((_, index) => index !== linkIndex);
    await onUpdate(item.id, {
      links: updatedLinks
    });
    setLinkMenuIndex(null);
  };

  const handleEditLink = (linkIndex) => {
    const link = item.links[linkIndex];
    setEditingLinkIndex(linkIndex);
    setNewLinkTitle(link.title);
    setNewLinkUrl(link.url);
    setLinkMenuIndex(null);
  };

  const handleSaveLinkEdit = async () => {
    if (!newLinkUrl.trim()) {
      alert('Please enter a URL');
      return;
    }

    const updatedLinks = [...(item.links || [])];
    updatedLinks[editingLinkIndex] = {
      ...updatedLinks[editingLinkIndex],
      title: newLinkTitle.trim() || newLinkUrl,
      url: newLinkUrl.trim(),
      updatedAt: new Date().toISOString()
    };

    await onUpdate(item.id, {
      links: updatedLinks
    });
    setEditingLinkIndex(null);
    setNewLinkTitle('');
    setNewLinkUrl('');
  };

  const handleCancelLinkEdit = () => {
    setEditingLinkIndex(null);
    setNewLinkTitle('');
    setNewLinkUrl('');
  };

  const handleOpenLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setLinkMenuIndex(null);
  };

  const toggleSection = (section) => {
    // Close all sections first
    setShowTags(false);
    setShowParent(false);
    setShowLinks(false);
    setShowChildren(false);
    
    // Open the selected section
    if (section === 'tags') setShowTags(true);
    else if (section === 'parent') setShowParent(true);
    else if (section === 'links') setShowLinks(true);
    else if (section === 'children') setShowChildren(true);
  };

  const handleSaveEdit = async () => {
    await onUpdate(item.id, formData);
    onClose();
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to permanently delete this work item? This cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'workItems', item.id));
        onClose();
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Failed to delete item. Please try again.');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) 
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleAddNewTag = () => {
    const trimmedTag = newTag.trim();
    if (!trimmedTag) return;
    
    // Add to local tags if not already there
    if (!localTags.includes(trimmedTag)) {
      setLocalTags(prev => [...prev, trimmedTag]);
    }
    
    // Add to form data tags
    if (!formData.tags.includes(trimmedTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, trimmedTag]
      }));
    }
    
    setNewTag('');
  };

  const handleParentChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setFormData(prev => ({
        ...prev,
        parentId: null,
        parentType: null
      }));
    } else {
      const [parentId, parentType] = value.split('|');
      setFormData(prev => ({
        ...prev,
        parentId,
        parentType
      }));
    }
  };

  const getParentOptions = () => {
    if (item.type === 'Initiative') return [];
    
    if (item.type === 'Epic') {
      return allItems.filter(i => i.type === 'Initiative' && !i.archived);
    }
    
    if (item.type === 'Task') {
      return allItems.filter(i => (i.type === 'Epic' || i.type === 'Initiative') && !i.archived);
    }
    
    return [];
  };

  const getChildren = () => {
    return allItems.filter(i => i.parentId === item.id);
  };

  const getAvailableChildren = () => {
    if (item.type === 'Initiative') {
      // Initiatives can have Epics and Tasks as children
      return allItems.filter(i => 
        (i.type === 'Epic' || i.type === 'Task') && 
        i.id !== item.id && 
        i.parentId !== item.id &&
        !i.archived
      );
    }
    
    if (item.type === 'Epic') {
      // Epics can have Tasks as children
      return allItems.filter(i => 
        i.type === 'Task' && 
        i.id !== item.id && 
        i.parentId !== item.id &&
        !i.archived
      );
    }
    
    return [];
  };

  const handleAddChild = async (childId) => {
    const child = allItems.find(i => i.id === childId);
    if (child) {
      await onUpdate(childId, {
        parentId: item.id,
        parentType: item.type
      });
    }
  };

  const handleRemoveChild = async (childId) => {
    await onUpdate(childId, {
      parentId: null,
      parentType: null
    });
  };

  const handleOpenChild = (child) => {
    if (onNavigateToItem) {
      onNavigateToItem(child);
    }
  };

  const parentOptions = getParentOptions();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
          <div className="modal-header-controls" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1 }}>
            <select 
              value={formData.type} 
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              style={{ 
                flex: 1,
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="Task">Task</option>
              <option value="Epic">Epic</option>
              <option value="Initiative">Initiative</option>
            </select>
            
            <select 
              value={formData.division} 
              onChange={(e) => setFormData(prev => ({ ...prev, division: e.target.value }))}
              style={{ 
                flex: 1,
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">No Division</option>
              <option value="S&I">S&I</option>
              <option value="R&S">R&S</option>
              <option value="Common">Common</option>
            </select>
            
            <select 
              value={formData.status} 
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              style={{ 
                flex: 1,
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Blocked">Blocked</option>
              <option value="Complete">Complete</option>
            </select>
          </div>
          
          <button
            type="button"
            onClick={() => onShowRelatedItems(item)}
            className="show-related-button-header"
            title="Show related items in hierarchy"
            aria-label="Show related items in hierarchy"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="16" y="16" width="6" height="6" rx="1"></rect>
              <rect x="2" y="16" width="6" height="6" rx="1"></rect>
              <rect x="9" y="2" width="6" height="6" rx="1"></rect>
              <path d="M12 8v3"></path>
              <path d="M12 11l-3 3"></path>
              <path d="M12 11l3 3"></path>
            </svg>
          </button>
          
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          {/* Section Chips */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => toggleSection(showTags ? null : 'tags')}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                background: showTags ? 'white' : '#f3f4f6',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: showTags ? '0 2px 4px rgba(0,0,0,0.1)' : '0 1px 2px rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease'
              }}
            >
              Tags ({formData.tags.length})
            </button>
            
            <button
              type="button"
              onClick={() => toggleSection(showParent ? null : 'parent')}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                background: showParent ? 'white' : '#f3f4f6',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: showParent ? '0 2px 4px rgba(0,0,0,0.1)' : '0 1px 2px rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease'
              }}
            >
              Parent ({formData.parentId ? 1 : 0})
            </button>
            
            <button
              type="button"
              onClick={() => toggleSection(showLinks ? null : 'links')}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                background: showLinks ? 'white' : '#f3f4f6',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: showLinks ? '0 2px 4px rgba(0,0,0,0.1)' : '0 1px 2px rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease'
              }}
            >
              Links ({item.links ? item.links.length : 0})
            </button>
            
            {(item.type === 'Initiative' || item.type === 'Epic') && (
              <button
                type="button"
                onClick={() => toggleSection(showChildren ? null : 'children')}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: showChildren ? 'white' : '#f3f4f6',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  boxShadow: showChildren ? '0 2px 4px rgba(0,0,0,0.1)' : '0 1px 2px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s ease'
                }}
              >
                Children ({getChildren().length})
              </button>
            )}
          </div>

          {/* Tags Section */}
          {showTags && (
            <div className="form-group" style={{ padding: '1rem', background: '#fafafa', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div className="tag-chips-container" style={{ marginBottom: '0.5rem' }}>
                {localTags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleTagToggle(tag)}
                    className={`tag-chip ${formData.tags.includes(tag) ? 'tag-chip-active' : ''}`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddNewTag())}
                  placeholder="Add new tag..."
                  style={{ flex: 1, padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd' }}
                />
                <button
                  type="button"
                  onClick={handleAddNewTag}
                  className="button button-primary small-button"
                >
                  Add Tag
                </button>
              </div>
            </div>
          )}

          {/* Parent Section */}
          {showParent && parentOptions.length > 0 && (
            <div className="form-group" style={{ padding: '1rem', background: '#fafafa', borderRadius: '8px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <label style={{ margin: 0, minWidth: 'fit-content' }}>Parent</label>
              <select 
                value={formData.parentId ? `${formData.parentId}|${formData.parentType}` : ''}
                onChange={handleParentChange}
                style={{ flex: 1 }}
              >
                <option value="">None</option>
                {parentOptions.map(parent => (
                  <option key={parent.id} value={`${parent.id}|${parent.type}`}>
                    {parent.title} ({parent.type})
                  </option>
                ))}
              </select>
            </div>
          )}

          {showParent && parentOptions.length === 0 && (
            <div className="form-group" style={{ padding: '1rem', background: '#fafafa', borderRadius: '8px', border: '1px solid #e5e7eb', color: '#666', fontSize: '14px' }}>
              No parent options available for this item type.
            </div>
          )}

          {/* Links Section */}
          {showLinks && (
            <div className="form-group" style={{ padding: '1rem', background: '#fafafa', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              {/* Existing Links Display */}
              {item.links && item.links.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                  {item.links.map((link, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <button
                        type="button"
                        onClick={() => setLinkMenuIndex(linkMenuIndex === index ? null : index)}
                        className="tag"
                        style={{ 
                          cursor: 'pointer', 
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px'
                        }}
                      >
                        {link.title} ▾
                      </button>
                      {linkMenuIndex === index && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '4px',
                          background: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          zIndex: 1000,
                          minWidth: '120px'
                        }}>
                          <button
                            type="button"
                            onClick={() => handleOpenLink(link.url)}
                            style={{
                              display: 'block',
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f0f0f0'}
                            onMouseLeave={(e) => e.target.style.background = 'none'}
                          >
                            🔗 Open
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEditLink(index)}
                            style={{
                              display: 'block',
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f0f0f0'}
                            onMouseLeave={(e) => e.target.style.background = 'none'}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteLink(index)}
                            style={{
                              display: 'block',
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '14px',
                              color: '#dc3545',
                              borderTop: '1px solid #eee'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#fff5f5'}
                            onMouseLeave={(e) => e.target.style.background = 'none'}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Link Form */}
              {editingLinkIndex !== null ? (
                <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '6px', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '14px' }}>Edit Link</h4>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '13px', display: 'block', marginBottom: '0.25rem' }}>Link Title</label>
                    <input
                      type="text"
                      value={newLinkTitle}
                      onChange={(e) => setNewLinkTitle(e.target.value)}
                      placeholder="e.g., Requirements Doc"
                      style={{ width: '100%', padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '13px', display: 'block', marginBottom: '0.25rem' }}>URL</label>
                    <input
                      type="url"
                      value={newLinkUrl}
                      onChange={(e) => setNewLinkUrl(e.target.value)}
                      placeholder="https://..."
                      style={{ width: '100%', padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" onClick={handleSaveLinkEdit} className="button button-primary small-button">
                      Save Changes
                    </button>
                    <button type="button" onClick={handleCancelLinkEdit} className="button button-secondary small-button">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '6px' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '14px' }}>Add New Link</h4>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '13px', display: 'block', marginBottom: '0.25rem' }}>Link Title</label>
                    <input
                      type="text"
                      value={newLinkTitle}
                      onChange={(e) => setNewLinkTitle(e.target.value)}
                      placeholder="e.g., Requirements Doc"
                      style={{ width: '100%', padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '13px', display: 'block', marginBottom: '0.25rem' }}>URL</label>
                    <input
                      type="url"
                      value={newLinkUrl}
                      onChange={(e) => setNewLinkUrl(e.target.value)}
                      placeholder="https://..."
                      style={{ width: '100%', padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd' }}
                    />
                  </div>
                  <button type="button" onClick={handleAddLink} className="button button-primary small-button">
                    Add Link
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Children Section */}
          {showChildren && (item.type === 'Initiative' || item.type === 'Epic') && (
            <div className="form-group" style={{ padding: '1rem', background: '#fafafa', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              {/* Existing Children Display */}
              {getChildren().length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                  {getChildren().map((child) => (
                    <div key={child.id} style={{ position: 'relative' }}>
                      <button
                        type="button"
                        onClick={() => handleOpenChild(child)}
                        className="tag"
                        style={{ 
                          cursor: 'pointer', 
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <span>{child.title}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveChild(child.id);
                          }}
                          style={{
                            background: 'rgba(255,255,255,0.3)',
                            border: 'none',
                            borderRadius: '3px',
                            padding: '2px 6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: 'white'
                          }}
                        >
                          ✕
                        </button>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Child Dropdown */}
              {getAvailableChildren().length > 0 && (
                <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '6px' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '14px' }}>Add Child Item</h4>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <select 
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddChild(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      style={{ flex: 1, padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd' }}
                    >
                      <option value="">Select an item...</option>
                      {getAvailableChildren().map(child => (
                        <option key={child.id} value={child.id}>
                          {child.title} ({child.type})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {getAvailableChildren().length === 0 && getChildren().length === 0 && (
                <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '6px', color: '#666', fontSize: '14px' }}>
                  No available items to add as children.
                </div>
              )}
            </div>
          )}

          <div className="form-actions" style={{ justifyContent: 'space-between' }}>
            <button onClick={handleDelete} className="button button-danger">
              Delete
            </button>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={onClose} className="button button-secondary">
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="button button-primary">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkItemModal;
